import { supabase } from '../lib/supabase';
import { isFootballLabel, isFootballMatch } from '../utils/seasons';

// A rollout can have the tables without the corresponding write policies. In
// that case PostgREST returns 42501 and the still-supported matches model is the
// only writable gathering store for that installation.
export const shouldUseLegacyGatherings = (error) =>
  ['42501', '42P01', 'PGRST200', 'PGRST204', 'PGRST205'].includes(error?.code);

const LEGACY_LOCATION_SEPARATOR = ' · Locatie: ';

export const buildLegacyGathering = ({ groupId, title, startsAt, locationName, label }) => ({
  group_id: groupId,
  opponent: title,
  date: startsAt,
  // The legacy schema only accepts home/away. Preserve the free-form venue in
  // label and normalize it back to locationName when rows are read.
  location: 'home',
  label: label
    ? `${label}${LEGACY_LOCATION_SEPARATOR}${locationName}`
    : `Locatie: ${locationName}`,
});

export const normalizeLegacyGathering = (match) => {
  const matchWithLocation = {
    ...match,
    locationName: match?.locations?.name || match?.locationName || '',
  };
  const label = String(match?.label || '');
  const markerIndex = label.lastIndexOf(LEGACY_LOCATION_SEPARATOR);
  const locationOnly = label.startsWith('Locatie: ');
  if (markerIndex < 0 && !locationOnly) return { ...matchWithLocation, isFootballMatch: isFootballMatch(match) };

  return {
    ...matchWithLocation,
    isGathering: true,
    isFootballMatch: isFootballLabel(locationOnly ? '' : label.slice(0, markerIndex)),
    label: locationOnly ? null : label.slice(0, markerIndex) || null,
    locationName: locationOnly
      ? label.slice('Locatie: '.length)
      : label.slice(markerIndex + LEGACY_LOCATION_SEPARATOR.length),
  };
};

/** Join historic compatibility IDs to the canonical gathering without losing orders. */
export const mergeGatheringSources = (gatherings, legacyMatches) => {
  const used = new Set();
  const merged = [];
  for (const gathering of gatherings) {
    const mirrors = legacyMatches.filter((match) => match.id === gathering.id || (
      match.group_id === gathering.group_id &&
      new Date(match.date).getTime() === new Date(gathering.date).getTime() &&
      String(match.opponent || '').trim() === String(gathering.title || gathering.opponent || '').trim()
    ));
    mirrors.forEach((match) => used.add(match.id));
    if (gathering.status === 'cancelled') continue;
    merged.push({
      ...gathering,
      isFootballMatch: gathering.label ? isFootballLabel(gathering.label) : mirrors.some((match) => match.isFootballMatch),
      legacyMatchId: mirrors.find((match) => match.id === gathering.id)?.id || mirrors[0]?.id || gathering.id,
      matchIds: [...new Set([gathering.id, ...mirrors.map((match) => match.id)])],
    });
  }
  return [...merged, ...legacyMatches.filter((match) => !used.has(match.id) && match.status !== 'cancelled')]
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

/**
 * Resolve the matches row required by transactions.match_id. Existing sessions
 * from before the compatibility migration may have a differently-id'd legacy
 * match, so verify the row instead of assuming an upsert created it.
 */
export const ensureLegacyMatchId = async ({ match, groupId }) => {
  const existingById = await supabase
    .from('matches')
    .select('id')
    .eq('id', match.id)
    .maybeSingle();
  if (existingById.error) throw existingById.error;
  if (existingById.data?.id) return existingById.data.id;

  const startsAt = match.starts_at || match.date;
  const title = match.title || match.opponent;
  const equivalent = await supabase
    .from('matches')
    .select('id')
    .eq('group_id', groupId)
    .eq('date', startsAt)
    .limit(1)
    .maybeSingle();
  if (equivalent.error) throw equivalent.error;
  if (equivalent.data?.id) return equivalent.data.id;

  const legacyGathering = buildLegacyGathering({
    groupId,
    title,
    startsAt,
    locationName: match.locationName || match.location || 'Onbekende locatie',
    label: match.label,
  });
  const inserted = await supabase
    .from('matches')
    .insert({ id: match.id, ...legacyGathering })
    .select('id')
    .single();
  if (inserted.error) throw inserted.error;
  return inserted.data.id;
};

/**
 * Create a gathering in the new sessions model, while keeping installations
 * that have not run supabase_gatherings.sql usable through the matches table.
 */
export const createGathering = async ({ groupId, title, startsAt, locationId, locationName, label }) => {
  const locationResult = locationId
    ? { data: { id: locationId }, error: null }
    : await supabase
      .from('locations')
      .insert({ name: locationName, group_id: groupId })
      .select('id')
      .single();

  if (!locationResult.error) {
    const sessionResult = await supabase
      .from('sessions')
      .insert({
        group_id: groupId,
        location_id: locationResult.data.id,
        title,
        label: label || null,
        starts_at: startsAt,
      })
      .select('id')
      .single();

    if (!sessionResult.error) {
      await supabase
        .from('matches')
        .upsert({
          id: sessionResult.data.id,
          ...buildLegacyGathering({ groupId, title, startsAt, locationName, label }),
        }, {
          onConflict: 'id',
          ignoreDuplicates: true,
        });
      return { id: sessionResult.data.id, source: 'sessions' };
    }
    if (!shouldUseLegacyGatherings(sessionResult.error)) {
      throw sessionResult.error;
    }
  } else if (!shouldUseLegacyGatherings(locationResult.error)) {
    throw locationResult.error;
  }

  const legacyResult = await supabase
    .from('matches')
    .insert(buildLegacyGathering({ groupId, title, startsAt, locationName, label }))
    .select('id')
    .single();

  if (legacyResult.error) throw legacyResult.error;
  return { id: legacyResult.data.id, source: 'matches' };
};
